import { useEffect, useState, type JSX, type ReactNode } from 'react';
import {
  renditionFor,
  type GenerationAcceptedDto,
  type GenerationWarning,
  type PhotoDto,
  type RenditionDto,
} from '../api/library.js';
import type { UploadKind } from '../api/uploads.js';
import { Section, TextArea, TextField } from '../components/Fields.js';
import { useAuth } from '../auth/context.js';
import { userErrorMessage } from '../api/errors.js';
import { useI18n } from '../i18n/context.js';
import { PhotoList } from './PhotoSlots.js';
import { RenditionPanel } from './RenditionPanel.js';

type SaveState = 'idle' | 'saving' | 'saved';

/** Общая форма вещи и локации: у обеих анкета из двух полей и рендер без утверждения. */
export interface SimpleEntity {
  readonly id: string;
  readonly name: string;
  readonly descriptionRu: string | null;
  readonly promptEn: string | null;
  readonly photos: readonly PhotoDto[];
  readonly renditions: readonly RenditionDto[];
}

export interface SimpleEntityApi<T extends SimpleEntity> {
  get(id: string): Promise<T>;
  update(
    id: string,
    body: { name?: string; descriptionRu?: string | null; promptEn?: string | null },
  ): Promise<T>;
  composePrompt(id: string): Promise<T>;
  setPhotos(id: string, photos: readonly { assetId: string; order: number }[]): Promise<T>;
  ensureRendition(id: string, styleId: string): Promise<T>;
  generate(renditionId: string, bookId?: string): Promise<GenerationAcceptedDto>;
  edit(
    renditionId: string,
    variantId: string,
    guidance: string,
    bookId?: string,
  ): Promise<GenerationAcceptedDto>;
  selectVariant(renditionId: string, variantId: string): Promise<T>;
}

export function SimpleEditor<T extends SimpleEntity>({
  entity,
  api,
  styleId,
  bookId,
  uploadKind,
  maxPhotos,
  photoHint,
  labels,
  kindField,
  footer,
  readOnly = false,
  showPrompt = true,
  onChanged,
}: {
  entity: T;
  api: SimpleEntityApi<T>;
  styleId: string | null;
  bookId?: string;
  uploadKind: UploadKind;
  maxPhotos: number;
  photoHint: string;
  labels: { name: string; description: string };
  /** Категория вещи или вид локации — единственное, чем карточки различаются. */
  kindField: ReactNode;
  footer?: ReactNode;
  readOnly?: boolean;
  /** У вещей промпт полностью собирается под капотом; локации пока сохраняют редактор. */
  showPrompt?: boolean;
  onChanged(next: T): void;
}): JSX.Element {
  const { t } = useI18n();
  const { user } = useAuth();
  const [name, setName] = useState(entity.name);
  const [description, setDescription] = useState(entity.descriptionRu ?? '');
  const [promptEn, setPromptEn] = useState(entity.promptEn ?? '');
  const [pending, setPending] = useState(false);
  const [warnings, setWarnings] = useState<readonly GenerationWarning[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [detailsSaveState, setDetailsSaveState] = useState<SaveState>('idle');
  const [promptSaveState, setPromptSaveState] = useState<SaveState>('idle');

  useEffect(() => {
    setName(entity.name);
    setDescription(entity.descriptionRu ?? '');
    setPromptEn(entity.promptEn ?? '');
  }, [entity]);

  const rendition = styleId ? renditionFor(entity, styleId) : undefined;
  const run = (
    action: Promise<T>,
    onSuccess?: () => void,
    onFailure?: () => void,
  ): Promise<void> => {
    setPending(true);
    setError(null);
    return action
      .then((next) => {
        onChanged(next);
        onSuccess?.();
      })
      .catch((reason: unknown) => {
        setError(userErrorMessage(reason, t));
        onFailure?.();
      })
      .finally(() => setPending(false));
  };
  const saveDetails = (): Promise<void> => {
    setDetailsSaveState('saving');
    return run(
      api.update(entity.id, {
        name: name.trim(),
        descriptionRu: description.trim() || null,
      }),
      () => setDetailsSaveState('saved'),
      () => setDetailsSaveState('idle'),
    );
  };
  const savePrompt = (): Promise<void> => {
    setPromptSaveState('saving');
    return run(
      api.update(entity.id, { promptEn }),
      () => setPromptSaveState('saved'),
      () => setPromptSaveState('idle'),
    );
  };

  const generate = (): void => {
    if (!styleId) return;
    setPending(true);
    setError(null);
    void api
      .ensureRendition(entity.id, styleId)
      .then((withRendition) => {
        const target = renditionFor(withRendition, styleId);
        if (!target) return withRendition;
        return api.generate(target.id, bookId).then((accepted) => {
          setWarnings(accepted.warnings);
          return api.get(entity.id);
        });
      })
      .then(onChanged)
      .catch((reason: unknown) => setError(userErrorMessage(reason, t)))
      .finally(() => setPending(false));
  };

  return (
    <div className="grid gap-6">
      {error ? (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      ) : null}
      <fieldset disabled={readOnly} className="grid items-start gap-5 sm:grid-cols-2">
        <TextField
          label={labels.name}
          value={name}
          onChange={(value) => {
            setDetailsSaveState('idle');
            setName(value);
          }}
        />
        {kindField}
        <TextArea
          label={labels.description}
          value={description}
          onChange={(value) => {
            setDetailsSaveState('idle');
            setDescription(value);
          }}
        />
      </fieldset>
      {readOnly ? null : (
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="kitup-secondary-button"
            disabled={pending}
            onClick={() => void saveDetails()}
          >
            {detailsSaveState === 'saving' ? t.common.saving : t.library.actions.save}
          </button>
          {detailsSaveState === 'saved' ? (
            <span role="status" className="text-sm text-emerald-700">
              {t.common.saved}
            </span>
          ) : null}
        </div>
      )}

      <Section title={t.library.photos.title}>
        <PhotoList
          photos={entity.photos}
          max={maxPhotos}
          kind={uploadKind}
          hint={photoHint}
          disabled={!(user?.consentPhotoProcessing ?? false)}
          readOnly={readOnly}
          onChange={(photos) => run(api.setPhotos(entity.id, photos))}
        />
      </Section>

      {showPrompt ? (
        <Section title={t.library.prompt.title} description={t.library.prompt.hint}>
          <fieldset disabled={readOnly}>
            <TextArea
              label={t.library.prompt.title}
              value={promptEn}
              rows={4}
              onChange={(value) => {
                setPromptSaveState('idle');
                setPromptEn(value);
              }}
            />
          </fieldset>
          {readOnly ? null : (
            <div className="mt-3 flex flex-wrap gap-3">
              <button
                type="button"
                className="kitup-secondary-button"
                disabled={pending}
                onClick={() => run(api.composePrompt(entity.id))}
              >
                {t.library.prompt.compose}
              </button>
              <button
                type="button"
                className="kitup-quiet-button"
                disabled={pending}
                onClick={() => void savePrompt()}
              >
                {promptSaveState === 'saving' ? t.common.saving : t.library.actions.save}
              </button>
              {promptSaveState === 'saved' ? (
                <span role="status" className="self-center text-sm text-emerald-700">
                  {t.common.saved}
                </span>
              ) : null}
            </div>
          )}
        </Section>
      ) : null}

      <Section title={t.library.rendition.variantsTitle}>
        {footer}
        <RenditionPanel
          rendition={rendition}
          styleReady={Boolean(styleId)}
          pending={pending}
          warnings={warnings}
          readOnly={readOnly}
          onGenerate={generate}
          onSelect={(variantId) => rendition && run(api.selectVariant(rendition.id, variantId))}
          onEdit={(variantId, guidance) =>
            rendition
              ? run(
                  api
                    .edit(rendition.id, variantId, guidance, bookId)
                    .then(() => api.get(entity.id)),
                )
              : Promise.resolve()
          }
        />
      </Section>
    </div>
  );
}
